import * as Globals from "../../globals";
import {buildingGroup, isFreeWallMode, measurementsGroup, mousePosition, tmpGroup, wallMeshHBE_L} from "../../main";
import {copyVectorArray, findChildByName} from "../../utils/threeUtils";
import {highlightedObject, highlightingObject} from "../../utils/highlighting";
import {clearGroup, render} from "../../sceneManager";
import {regenerateRoof} from "../roofs/roofGeneration";
import {Box3, Object3D, Vector3} from "three";
import {regenerateFloor} from "../floor/floorGeneration";
import {createWallSegment, getIntersectingWalls, getWallOrientation} from "./wall";
import {createRooms} from "../rooms/roomGeneration";
import {recalculateBuildingParameters} from "../../calculators/buildingParameters/parameterCalculator";
import {CutOut} from "../cutOuts/cutOut";
import * as nameContants from "../../constants/nameConstants";
import {controlObjectName, cutOutsGroupName, roofGroupName} from "../../constants/nameConstants";
import {getWallGroupsByOrientation} from "../../utils/configuratorUtils";
import {sortBySubKey, toFixedVector} from "../../utils/utils";
import {areOverlapping} from "../../utils/mathUtils";
import {updateWallSnapAxis} from "./wallGeneration";
import {clearLiveMeasurementState, generateLiveMeasurement} from "../measurements/liveMeasurement";

let initialWallPosition;
let initialBounds = null;
let intersectingWalls = []; //Wände in der Szene die intersecten mit der aktuell gewählten und angepasst werden müssen
let previewWalls = [];
let initialWalls = [];// Kopie der Ursprungswände
let selectedWall = null;
let equalOrientedWalls = []; //Wände die gleich orientiert zur aktuellen Wand sind. Über diese kann nicht rüber geschoben werden

export function startWallMovement(wallGroup, position) {
    selectedWall = wallGroup;
    initialWallPosition = position;
    initialBounds = new Box3().setFromObject(selectedWall);
    let walls = getIntersectingWalls(wallGroup); //Wände auswählen die ggf angepasst werden müssen

    initialWalls = [];
    for (let i = 0; i < walls.length; i++) {
        initialWalls.push(new Object3D().copy(walls[i]));
    }
    intersectingWalls = walls;

    //Alle gleich orientierten Wände laden
    let wallOrientation = getWallOrientation(wallGroup);
    let walls2 = getWallGroupsByOrientation(wallOrientation);
    equalOrientedWalls = [];
    for (let i = 0; i < walls2.length; i++) {
        let wall = walls2[i];
        let bounds = new Box3().setFromObject(wall);
        let center = bounds.getCenter(new Vector3());
        equalOrientedWalls.push({
            wallGroup: wall,
            bounds: bounds,
            min: bounds.min,
            max: bounds.max,
            center: center,
            isInitalWall: wall === wallGroup
        });
    }

    //Aufsteigen Sortierung der Wände nach Position
    if (equalOrientedWalls.length > 1) {
        equalOrientedWalls = sortBySubKey(equalOrientedWalls, 'min', wallOrientation === "x" ? "z" : "x");
    }

    clearGroup(measurementsGroup);
    clearGroup(tmpGroup);
}

export function resetWallMovement(wallGroup) {
    wallGroup.position.set(initialWallPosition.x, initialWallPosition.y, initialWallPosition.z);

    //Restore initialWalls
    for (let i = 0; i < initialWalls.length; i++) {
        if (initialWalls[i] === intersectingWalls[i]) {
            continue;
        }

        //Replace adjustedWall with original wall
        clearGroup(intersectingWalls[i]);
        intersectingWalls[i].removeFromParent();
        buildingGroup.addWithUuid(initialWalls[i]);
    }

    initialWalls = []; //Array leeren um zu verhindern dass die Wände gelöscht werden
    clearWallMovementState();
}

export function applyWallMovement() {
    let roofPoints = [];
    let roof = findChildByName(buildingGroup, roofGroupName);

    if (roof !== null) {
        roofPoints = copyVectorArray(roof.userData.roof.points, true);
    }

    let moveVector = new Vector3().subVectors(selectedWall.position, initialWallPosition);
    if (selectedWall.userData.roof !== undefined && selectedWall.userData.roof.roofPoints !== undefined) {
        for (let i = 0; i < selectedWall.userData.roof.roofPoints.length; i++) {
            selectedWall.userData.roof.roofPoints[i].point.add(moveVector);
            selectedWall.userData.roof.roofPoints[i].point = toFixedVector(selectedWall.userData.roof.roofPoints[i].point);
            let index = selectedWall.userData.roof.roofPoints[i].index;
            roofPoints[index].add(moveVector);
            roofPoints[index] = toFixedVector(roofPoints[index]);
        }
    }

    let selectedWallBounds = new Box3().copy(initialBounds).expandByVector(new Vector3(0.11, 0.11, 0.11)); //Expand um 0.11 (Wanddicke + 0.01 Toleranz
    selectedWallBounds.min = toFixedVector(selectedWallBounds.min);
    selectedWallBounds.max = toFixedVector(selectedWallBounds.max);
    let selectedWallCutouts = findChildByName(selectedWall, cutOutsGroupName);
    for (let i = 0; i < selectedWallCutouts.children.length; i++) {
        //Update controlObjects saved scene position
        let cutOutGroup = selectedWallCutouts.children[i];
        let controlObject = findChildByName(cutOutGroup, controlObjectName);
        controlObject.userData.originalParent = controlObject.parent.uuid;
        controlObject.userData.sceneMatrix.position.add(moveVector);
    }

    for (let i = 0; i < intersectingWalls.length; i++) {
        //Verschiebung relevanter Dachpunkte
        let wall = intersectingWalls[i];
        if (wall.userData.roof !== undefined && wall.userData.roof.roofPoints !== undefined) {
            for (let j = 0; j < wall.userData.roof.roofPoints.length; j++) {
                let roofPoint = wall.userData.roof.roofPoints[j].point;
                roofPoint = toFixedVector(roofPoint);
                if (selectedWallBounds.containsPoint(roofPoint)) {
                    wall.userData.roof.roofPoints[j].point.add(moveVector);
                    wall.userData.roof.roofPoints[j].point = toFixedVector(wall.userData.roof.roofPoints[j].point);
                    let index = wall.userData.roof.roofPoints[j].index;
                    roofPoints[index].add(moveVector);
                    roofPoints[index] = toFixedVector(roofPoints[index]);
                }
            }
        }

        let selectedWallCutouts = findChildByName(wall, cutOutsGroupName);
        for (let i = 0; i < selectedWallCutouts.children.length; i++) {
            //Update controlObjects saved scene position
            let cutOutGroup = selectedWallCutouts.children[i];
            let controlObject = findChildByName(cutOutGroup, controlObjectName);
            controlObject.userData.originalParent = controlObject.parent.uuid;
            controlObject.userData.sceneMatrix.position = controlObject.getWorldPosition(new Vector3());
            controlObject.userData.groupMatrix.position = controlObject.position;
        }

        //Regenerierung der existierenden Cutouts
        CutOut.adjustWall(wall);
    }

    if (roof !== null) {
        roof.userData.roof.points = roofPoints;
    }
    render();
    clearWallMovementState();
    createRooms();
    regenerateRoof();
    regenerateFloor();
    recalculateBuildingParameters().then();
    updateWallSnapAxis();
}

export function clearWallMovementState() {
    initialWallPosition = null;
    initialBounds = null;
    selectedWall = null;
    for (let i = 0; i < initialWalls.length; i++) {
        clearGroup(initialWalls[i]);
        initialWalls[i].removeFromParent();
    }
    for (let i = 0; i < previewWalls.length; i++) {
        if (!intersectingWalls.includes(previewWalls[i])) {
            clearGroup(previewWalls[i]);
            previewWalls[i].removeFromParent();
        }
    }
    previewWalls = [];
    intersectingWalls = [];
    initialWalls = [];
    equalOrientedWalls = [];
}

// wird bei Mausbewegung aufgerufen
export function generateMoveWallPreview(wallGroup) {
    try {
        let wallPosition = new Vector3().copy(wallGroup.getWorldPosition(new Vector3()));
        let positionDelta = new Vector3().copy(mousePosition).sub(wallPosition); //Mausposition kopieren, Weltposition der Wandgruppe abziehen > Differenz
        let wallOrientation = getWallOrientation(wallGroup);
        let translationAmount = wallOrientation === "x" ? positionDelta.z : positionDelta.x;
        let elementWidthDelta;
        let isOuterWall = !(wallGroup.userData.isOuterWall === undefined || !wallGroup.userData.isOuterWall);

        let offset;
        if (isFreeWallMode /* && !isOuterWall */) {
            elementWidthDelta = Math.round(translationAmount / 0.01) *0.01;
            offset = new Vector3(0, 0, 0);
        } else {
            elementWidthDelta = Math.round(translationAmount / Globals.HBESectionWidth) * Globals.HBESectionWidth;
            offset = new Vector3(0, 0, 0);
        }

        if (Math.abs(elementWidthDelta) <= 0) {
            return;
        }

        if (isOuterWall) {
            elementWidthDelta = getValidWallMovementDelta(elementWidthDelta, wallGroup, wallOrientation);

            if (elementWidthDelta === 0) {
                return;
            }
        }

        //Verschiebung der Wand muss immer entgegen der Wandorientierung sein
        if (wallOrientation === "x") {
            wallGroup.position.z += elementWidthDelta + offset.z;
            highlightingObject.position.z += elementWidthDelta + offset.z;
        } else {
            wallGroup.position.x += elementWidthDelta + offset.x;
            highlightingObject.position.x += elementWidthDelta + offset.x;
        }

        if (isOuterWall) {
            adjustIntersectingWalls(intersectingWalls, wallGroup, elementWidthDelta, wallPosition); //Anpassen von relevanten Wänden
        }

        clearLiveMeasurementState();
        generateLiveMeasurement({type: nameContants.wallGroupName, object: highlightedObject});
        render();

        //TODO: Ggf live Bemaßung hat aber aktuell großen Performanceeinfluss
        // generateMeasurements();
    } catch (e) {
        console.error(e);
        resetWallMovement(selectedWall);
    }
}

function getValidWallMovementDelta(elementWidthDelta, wallGroup, wallOrientation) {
    let steps = Math.abs(elementWidthDelta / 0.545);
    let wallBounds = new Box3().setFromObject(wallGroup);
    wallBounds.min.y = 0;
    wallBounds.max.y = 1;

    let initialWallIndex = equalOrientedWalls.findIndex(x => x.isInitalWall); //Ursprungsindex der Wand im Array

    for (let i = 1; i <= steps; i++) {
        let movedWallBounds = new Box3().copy(wallBounds);
        let currentDelta = elementWidthDelta > 0 ? i * 0.545 : i * -0.545;

        if (wallOrientation === "x") {
            movedWallBounds.min.z += currentDelta;
            movedWallBounds.max.z += currentDelta;
        } else {
            movedWallBounds.min.x += currentDelta;
            movedWallBounds.max.x += currentDelta;
        }

        //Prüfen ob die Wand eine im 90 Grad Winkel stehende Wand bzw. einen Ausschnitt darin schneiden würde
        for (let j = 0; j < intersectingWalls.length; j++) {
            let intersectingWall = intersectingWalls[j];
            let selectedWallCutouts = findChildByName(intersectingWall, cutOutsGroupName);
            for (let k = 0; k < selectedWallCutouts.children.length; k++) {
                //Update controlObjects saved scene position
                let cutOutGroup = selectedWallCutouts.children[k];
                let controlObject = findChildByName(cutOutGroup, controlObjectName);
                let controlObjectBounds = new Box3().setFromObject(controlObject);
                controlObjectBounds.min.y = 0;
                controlObjectBounds.max.y = 1;

                if (!new Box3().copy(movedWallBounds).intersect(controlObjectBounds).isEmpty()) {
                    return elementWidthDelta > 0 ? (i - 1) * 0.545 : (i - 1) * -0.545;
                }
            }
        }

        //Mindestens eine weitere Wand neben der Wand an der Maus
        if (equalOrientedWalls.length > 1) {
            //Prüfen, ob eine Wand eine gleich orientierte Wand überschreiten würde

            //Wände sind aufsteigend nach Position sortiert.
            //Um trivial zu prüfen, ob die Wand eine andere überschritten hat, werden die neuen Bounds für die gewählte Wand gesetzt in einer Kopie.
            //Anschließend wird das Array wieder sortiert und der Index darf sich nicht verändert haben.
            //Anschließend brauch nur eine tatsächliche Überlappung mit der Wand direkt vor und nach der aktuellen Wand geprüft werden.

            //InitialWallIndex wird zuvor berechnet außerhalb Loop. Ist immer der gleiche
            let equalOrientedWallsCopy = [...equalOrientedWalls]; //Kopie des Array

            //Anwenden der neuen theoretischen bounds wenn die Wand gültig ist
            equalOrientedWallsCopy[initialWallIndex].bounds = movedWallBounds;
            equalOrientedWallsCopy[initialWallIndex].min = movedWallBounds.min;
            equalOrientedWallsCopy[initialWallIndex].max = movedWallBounds.max;

            //Aufsteigen Sortierung der Wände nach Position
            equalOrientedWallsCopy = sortBySubKey(equalOrientedWallsCopy, 'min', wallOrientation === "x" ? "z" : "x");

            let newWallIndex = equalOrientedWallsCopy.findIndex(x => x.isInitalWall); //Index der Wand im Array

            if (initialWallIndex !== newWallIndex) {
                //Position ungültig eine Wand wurde überschritten
                return elementWidthDelta > 0 ? (i - 1) * 0.545 : (i - 1) * -0.545; //Zurücksetzen um eine Position
            }

            //Brauchen wir nicht weiter prüfen, wenn der triviale Fall schon ungültig ist. Dann muss die Wand ein Element zurückgesetzt bzw. an die Ursprungsposition gesetzt werden.
            //Prüfen, ob die Wand mit der davor oder danach überschneidet.
            let initialWall = equalOrientedWallsCopy[newWallIndex];
            let initalWallRange = wallOrientation === "x" ?
                {start: initialWall.min.z, finish: initialWall.max.z} :
                {start: initialWall.min.x, finish: initialWall.max.x};

            let previousWallRange = null;
            if (newWallIndex > 0) {
                let previousWall = equalOrientedWallsCopy[newWallIndex - 1];
                previousWallRange = wallOrientation === "x" ?
                    {start: previousWall.min.z, finish: previousWall.max.z} :
                    {start: previousWall.min.x, finish: previousWall.max.x};
            }

            let nextWallRange = null;
            if (newWallIndex < equalOrientedWallsCopy.length - 1) {
                let nextWall = equalOrientedWallsCopy[newWallIndex + 1];
                nextWallRange = wallOrientation === "x" ?
                    {start: nextWall.min.z, finish: nextWall.max.z} :
                    {start: nextWall.min.x, finish: nextWall.max.x};
            }

            if (previousWallRange !== null) {
                if (areOverlapping(initalWallRange, previousWallRange, true)) {
                    return elementWidthDelta > 0 ? (i - 1) * 0.545 : (i - 1) * -0.545; //Zurücksetzen um eine Position
                }
            }

            if (nextWallRange !== null) {
                if (areOverlapping(initalWallRange, nextWallRange, true)) {
                    return elementWidthDelta > 0 ? (i - 1) * 0.545 : (i - 1) * -0.545; //Zurücksetzen um eine Position
                }
            }
        }
    }

    return elementWidthDelta;
}

export function adjustIntersectingWalls(intersectingWalls, selectedWallGroup, sizeDifference, originalWallPosition) {
    //Stretch intersecting walls to new length
    //Recreate cutouts for all walls
    //Regenerate roof & floor
    let newSelectedWallPosition = selectedWallGroup.getWorldPosition(new Vector3());
    let wallOrientation = getWallOrientation(selectedWallGroup);

    for (let i = 0; i < intersectingWalls.length; i++) {
        let intersectingWallGroup = intersectingWalls[i];
        let intersectingWallOrientation = getWallOrientation(intersectingWallGroup);

        if (intersectingWallOrientation === wallOrientation) {
            continue; //Nur Wände beachten die im rechten Winkel zueinander stehen
        }

        let intersectingWallPosition = intersectingWallGroup.getWorldPosition(new Vector3());

        let parent = intersectingWallGroup.parent;
        let userData = intersectingWallGroup.userData;
        let position = intersectingWallGroup.position;
        let rotation = intersectingWallGroup.rotation;
        let name = intersectingWallGroup.name;
        let isOuterWall = intersectingWallGroup.userData.isOuterWall;
        let direction = intersectingWallGroup.userData.direction;

        let sizeDifferenceAbs = Math.abs(sizeDifference);

        //Prüfung, ob der Wandabstand kleiner geworden ist > Differenz muss abgezogen werden sonst addiert
        let previousDelta, currentDelta = null;
        if (intersectingWallOrientation === "x") {
            previousDelta = Math.abs(originalWallPosition.x - intersectingWallPosition.x);
            currentDelta = Math.abs(newSelectedWallPosition.x - intersectingWallPosition.x);
        } else {
            previousDelta = Math.abs(originalWallPosition.z - intersectingWallPosition.z);
            currentDelta = Math.abs(newSelectedWallPosition.z - intersectingWallPosition.z);
        }

        let addOffset = true;
        if (currentDelta < previousDelta) {
            sizeDifferenceAbs *= -1; //Invertiere Größendifferenz, wenn Wand ineinander geschoben wurde. Sonst positiv lassen
            addOffset = false;
        }

        let newWall = createWallSegment(parseFloat(userData.length) + sizeDifferenceAbs, userData.height, wallMeshHBE_L, Globals.layers.wall, findChildByName(intersectingWallGroup, "schwelleMesh") !== null, true, direction, isOuterWall);

        parent.addWithUuid(newWall);
        if (intersectingWalls[i].userData.roof !== undefined) {
            newWall.userData.roof = {...intersectingWalls[i].userData.roof};
        }
        intersectingWalls[i] = newWall;
        newWall.position.set(position.x, position.y, position.z);

        if (intersectingWallOrientation === "x") {
            newWall.position.x = newWall.position.x + (0.5 * sizeDifference);
        } else {
            newWall.position.z = newWall.position.z + (0.5 * sizeDifference);
        }
        newWall.rotation.set(rotation.x, rotation.y, rotation.z);
        newWall.name = name;
        newWall.userData.isOuterWall = isOuterWall;

        intersectingWallGroup.removeFromParent();
        let cutoutsGroup = findChildByName(intersectingWallGroup, cutOutsGroupName);
        let newCutoutGroup = findChildByName(newWall, cutOutsGroupName);
        clearGroup(newCutoutGroup);
        newCutoutGroup.removeFromParent();
        newWall.addWithUuid(cutoutsGroup.clone(true));
        let newCutoutsGroup = findChildByName(newWall, cutOutsGroupName);

        for (let j = 0; j < newCutoutsGroup.children.length; j++) {
            let oldControlObject = findChildByName(cutoutsGroup.children[j], controlObjectName);
            let worldPosition = oldControlObject.getWorldPosition(new Vector3());
            let newControlObject = findChildByName(newCutoutsGroup.children[j], controlObjectName);
            let newWorldPosition = newControlObject.getWorldPosition(new Vector3());

            let diff = new Vector3().subVectors(worldPosition, newWorldPosition);
            if (intersectingWallOrientation === "z") {
                diff.x = diff.z;
            }
            diff.y = 0;
            diff.z = 0;

            if (newWall.rotation.z === 0 || newWall.rotation.z.toFixed(4) === (Math.PI / 2).toFixed(4)) {
                newControlObject.parent.children.forEach((child) => {
                    child.position.add(diff);
                });
            } else {
                newControlObject.parent.children.forEach((child) => {
                    child.position.sub(diff);
                });
            }
        }

        // render();

        previewWalls.push(newWall);
    }

    Globals.setActiveMode(Globals.modes.moveWall);
}
